import { FormEvent, useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import MediaGalleryModal, { MediaAsset } from '@baaligames/shared-ui/admin/components/MediaGalleryModal';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { INPUT_CLS, INPUT_SM_CLS } from '../../components/fieldStyles';

type Referral = {
  id: number;
  status: 'pending' | 'credited' | 'rejected';
  bonus_amount: number | null;
  created_at: string;
  credited_at: string | null;
  rejection_reason: string | null;
  triggered_by_recharge_id: number | null;
  referrer_id: number;
  referrer_username: string;
  referrer_code: string;
  referred_id: number;
  referred_username: string;
};

type Stats = {
  total: number;
  pending: number;
  credited: number;
  rejected: number;
  total_paid: number;
};

type ListResp = {
  rows: Referral[];
  total: number;
  limit: number;
  offset: number;
  stats: Stats;
};

type Config = {
  active: boolean;
  bonus_type: 'percent' | 'fixed';
  bonus_amount: number;
  min_recharge_to_trigger: number;
  max_bonus_per_referral: number;
  // 2026-05-20 — admin-uploaded image rendered on every player's
  // profile Referral card. Null = use the frontend default art.
  referral_card_image_url?: string | null;
};

const PAGE = 20;

const STATUS_COLORS: Record<Referral['status'], string> = {
  pending:  'bg-amber-600/20 text-amber-700 dark:text-amber-300 border-amber-600/30',
  credited: 'bg-emerald-600/20 text-emerald-700 dark:text-emerald-300 border-emerald-600/30',
  rejected: 'bg-red-600/20 text-red-700 dark:text-red-300 border-red-600/30',
};

export default function ReferralsPanel() {
  const [list, setList] = useState<ListResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState<'' | Referral['status']>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const q = new URLSearchParams();
      q.set('limit', String(PAGE));
      q.set('offset', String(page * PAGE));
      if (status) q.set('status', status);
      if (search.trim()) q.set('search', search.trim());
      const d = await adminApi.get<ListResp>(`/admin/referrals?${q.toString()}`);
      setList(d);
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setLoading(false); }
  }, [status, search, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [status, search]);

  function note(msg: string) { setFlash(msg); setTimeout(() => setFlash(null), 3000); }

  async function reject(r: Referral) {
    const reason = window.prompt('Reason for rejection? (kept in the audit log)', 'No valid recharge within 30 days');
    if (!reason) return;
    try {
      await adminApi.post(`/admin/referrals/${r.id}/reject`, { reason });
      note(`Referral rejected`);
      load();
    } catch (e) { alert((e as ApiError).message); }
  }

  const totalPages = list ? Math.max(1, Math.ceil(list.total / PAGE)) : 1;

  return (
    <div className="space-y-4">
      {flash && (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          className="mb-4 rounded-xl bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 text-emerald-700 dark:text-emerald-200 px-4 py-2 text-sm inline-flex items-center gap-2">
          <Icon name="check_circle" size={16} filled /> {flash}
        </motion.div>
      )}

      <ConfigPanel onChange={load} />

      {list && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 my-5">
          <StatTile label="Total"    value={list.stats.total} />
          <StatTile label="Pending"  value={list.stats.pending}  color="text-amber-400" />
          <StatTile label="Credited" value={list.stats.credited} color="text-emerald-400" />
          <StatTile label="Rejected" value={list.stats.rejected} color="text-red-400" />
          <StatTile label="Total paid" value={`${list.stats.total_paid.toLocaleString('en-IN')}`} color="text-violet-400" />
        </div>
      )}

      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Icon name="search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-arena-600 dark:text-neon-200/60" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search username or code…"
            className={INPUT_CLS + ' pl-10'} />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}
          className={INPUT_CLS + ' w-auto'}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="credited">Credited</option>
          <option value="rejected">Rejected</option>
        </select>
        <button onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold bg-arena-100/60 dark:bg-arena-800 hover:bg-arena-100/60 dark:bg-arena-700/60 text-arena-800 dark:text-neon-200/90 transition">
          <Icon name="refresh" size={16} /> Refresh
        </button>
      </div>

      {loading && <div className="p-10 text-center text-arena-500 dark:text-neon-200/55"><Icon name="progress_activity" size={28} className="animate-spin inline-block" /> Loading…</div>}
      {err && <div className="p-4 rounded-xl bg-rose-500/10 ring-1 ring-inset ring-rose-500/30 text-red-700 dark:text-red-200 text-sm">{err}</div>}

      {!loading && list && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl bg-white dark:bg-arena-800/60 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white dark:bg-arena-800/80 text-xs uppercase text-arena-400 dark:text-neon-200/70">
                <tr>
                  <th className="text-left px-4 py-2.5">Referrer</th>
                  <th className="text-left px-4 py-2.5">Referred user</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-right px-4 py-2.5">Bonus</th>
                  <th className="text-left px-4 py-2.5">Created</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {list.rows.map((r) => (
                  <tr key={r.id} className="border-t ring-arena-200/60 dark:ring-arena-600/40 hover:bg-arena-50/60 dark:bg-arena-700/60/30">
                    <td className="px-4 py-3">
                      <div className="text-neutral-900 dark:text-neutral-100 font-semibold">@{r.referrer_username}</div>
                      <code className="text-[10px] text-violet-700 dark:text-violet-300 font-mono">{r.referrer_code}</code>
                    </td>
                    <td className="px-4 py-3 text-arena-700 dark:text-neon-200/75">@{r.referred_username}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${STATUS_COLORS[r.status]}`}>
                        {r.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-neutral-900 dark:text-neutral-100">
                      {r.bonus_amount !== null ? `${r.bonus_amount}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-arena-600 dark:text-neon-200/60">
                      {new Date(r.created_at).toLocaleDateString()}
                      {r.credited_at && <span className="block text-[10px] text-arena-700 dark:text-neon-200/80">→ {new Date(r.credited_at).toLocaleDateString()}</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.status === 'pending' ? (
                        <div className="flex gap-1 justify-end">
                          <ManualCreditButton referral={r} onDone={load} />
                          <button onClick={() => reject(r)}
                            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold bg-red-600/20 hover:bg-red-600/40 text-red-700 dark:text-red-200 transition">
                            <Icon name="close" size={12} /> Reject
                          </button>
                        </div>
                      ) : (
                        r.rejection_reason && (
                          <span className="text-[11px] text-arena-600 dark:text-neon-200/60 italic">{r.rejection_reason}</span>
                        )
                      )}
                    </td>
                  </tr>
                ))}
                {list.rows.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-arena-600 dark:text-neon-200/60">
                    No referrals yet. They appear here when someone signs up with another user's referral code.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {list && list.total > PAGE && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-arena-100/60 dark:bg-arena-800 hover:bg-arena-100/60 dark:bg-arena-700/60 text-arena-800 dark:text-neon-200/90 disabled:opacity-40 disabled:cursor-not-allowed transition">
            <Icon name="chevron_left" size={16} /> Prev
          </button>
          <span className="text-arena-600 dark:text-neon-200/60">Page {page + 1} / {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-arena-100/60 dark:bg-arena-800 hover:bg-arena-100/60 dark:bg-arena-700/60 text-arena-800 dark:text-neon-200/90 disabled:opacity-40 disabled:cursor-not-allowed transition">
            Next <Icon name="chevron_right" size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, color = 'text-arena-800 dark:text-neon-200/90' }: { label: string; value: number | string; color?: string }) {
  return (
    <div className="rounded-xl bg-white dark:bg-arena-800/60 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-3">
      <div className="text-[10px] uppercase text-arena-600 dark:text-neon-200/60 font-bold">{label}</div>
      <div className={`mt-1 text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
}

function ManualCreditButton({ referral, onDone }: { referral: Referral; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('50');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi.post(`/admin/referrals/${referral.id}/credit`, {
        bonusAmount: Math.max(0, Math.round(Number(amount) || 0)),
      });
      setOpen(false);
      onDone();
    } catch (e) {
      alert((e as ApiError).message);
    } finally { setBusy(false); }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-700 dark:text-emerald-200 transition">
        <Icon name="payments" size={12} /> Credit
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="inline-flex items-center gap-1">
      <input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)}
        className={INPUT_SM_CLS + ' w-16 text-right'} autoFocus />
      <button type="submit" disabled={busy}
        className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40 transition">
        <Icon name="check" size={12} />
      </button>
      <button type="button" onClick={() => setOpen(false)}
        className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold bg-arena-100/60 dark:bg-arena-800 hover:bg-arena-100/60 dark:bg-arena-700/60 text-arena-800 dark:text-neon-200/90 transition">
        <Icon name="close" size={12} />
      </button>
    </form>
  );
}

function ConfigPanel({ onChange }: { onChange: () => void }) {
  const [cfg, setCfg] = useState<Config | null>(null);
  const [draft, setDraft] = useState<Config | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<number | null>(null);
  const [previewAmount, setPreviewAmount] = useState('500');
  const [err, setErr] = useState<string | null>(null);
  // 2026-05-20 — Referral Card image picker modal state.
  const [cardGalleryOpen, setCardGalleryOpen] = useState(false);

  useEffect(() => {
    adminApi.get<{ config: Config }>('/admin/referrals/config')
      .then(({ config }) => { setCfg(config); setDraft(config); });
  }, []);

  useEffect(() => {
    if (!draft) return;
    const amt = Math.max(0, Math.round(Number(previewAmount) || 0));
    adminApi.get<{ bonus: number }>(`/admin/referrals/preview?amount=${amt}`)
      .then(({ bonus }) => setPreview(bonus))
      .catch(() => setPreview(null));
  }, [draft, previewAmount]);

  if (!cfg || !draft) {
    return <div className="rounded-2xl bg-white dark:bg-arena-800/60 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-4 text-xs text-arena-600 dark:text-neon-200/60">Loading config…</div>;
  }

  const dirty = JSON.stringify(cfg) !== JSON.stringify(draft);

  async function save() {
    setBusy(true); setErr(null);
    try {
      const { config } = await adminApi.put<{ config: Config }>('/admin/referrals/config', draft);
      setCfg(config); setDraft(config);
      onChange();
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setBusy(false); }
  }

  const setField = <K extends keyof Config>(k: K, v: Config[K]) => setDraft({ ...draft, [k]: v });

  return (
    <div className="rounded-2xl bg-gradient-to-br from-violet-900/20 to-purple-900/20 ring-1 ring-inset ring-violet-700/30 p-5 mb-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="settings" size={20} className="text-violet-700 dark:text-violet-300" filled />
        <h3 className="font-bold text-neutral-900 dark:text-neutral-100">Referral bonus settings</h3>
        <label className="ml-auto inline-flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={draft.active}
            onChange={(e) => setField('active', e.target.checked)}
            className="w-4 h-4 accent-violet-500" />
          <span className={draft.active ? 'text-emerald-400 font-semibold' : 'text-arena-600'}>
            {draft.active ? 'Active' : 'Paused'}
          </span>
        </label>
      </div>
      <p className="text-[11px] text-arena-600 dark:text-neon-200/60 mb-4">
        When a user signs up with someone's referral code and later makes a recharge at or above the minimum, the
        referrer automatically gets a bonus credited to their wallet. Weights below drive the calculation.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[10px] uppercase text-arena-600 dark:text-neon-200/60 font-bold mb-1">Bonus type</label>
          <div className="flex gap-2">
            <button type="button" onClick={() => setField('bonus_type', 'percent')}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border transition
                ${draft.bonus_type === 'percent' ? 'bg-violet-600 text-white shadow-[0_2px_10px_rgba(139,92,246,0.4)]' : 'bg-arena-100/60 dark:bg-arena-800 text-arena-700 dark:text-neon-200/75 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 hover:ring-arena-300/70 dark:hover:ring-arena-500/50'}`}>
              Percent of recharge
            </button>
            <button type="button" onClick={() => setField('bonus_type', 'fixed')}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border transition
                ${draft.bonus_type === 'fixed' ? 'bg-violet-600 text-white shadow-[0_2px_10px_rgba(139,92,246,0.4)]' : 'bg-arena-100/60 dark:bg-arena-800 text-arena-700 dark:text-neon-200/75 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 hover:ring-arena-300/70 dark:hover:ring-arena-500/50'}`}>
              Fixed amount
            </button>
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase text-arena-600 dark:text-neon-200/60 font-bold mb-1">
            {/* Batch 3 #20: was rendering bare "( fixed)" — empty unit before
                "fixed". Add the rupee symbol so admins know the unit. */}
            Bonus {draft.bonus_type === 'percent' ? '(% of recharge)' : '(₹ fixed)'}
          </label>
          <input type="number" min={0} max={draft.bonus_type === 'percent' ? 100 : 100000}
            value={draft.bonus_amount} onChange={(e) => setField('bonus_amount', Math.max(0, Number(e.target.value) || 0))}
            className={INPUT_CLS + ' font-mono'} />
        </div>
        <div>
          <label className="block text-[10px] uppercase text-arena-600 dark:text-neon-200/60 font-bold mb-1">
            {/* Batch 3 #20: was rendering "Minimum recharge to trigger ()" —
                the currency unit ₹ was missing inside the parens. */}
            Minimum recharge to trigger (₹)
          </label>
          <input type="number" min={0} value={draft.min_recharge_to_trigger}
            onChange={(e) => setField('min_recharge_to_trigger', Math.max(0, Math.floor(Number(e.target.value) || 0)))}
            className={INPUT_CLS + ' font-mono'} />
          <p className="text-[11px] text-arena-600 dark:text-neon-200/60 mt-1">Recharges below this amount don't pay out a bonus.</p>
        </div>
        <div>
          <label className="block text-[10px] uppercase text-arena-600 dark:text-neon-200/60 font-bold mb-1">
            {/* Batch 3 #20: was rendering "Maximum bonus per referral ()" —
                the currency unit ₹ was missing inside the parens. */}
            Maximum bonus per referral (₹)
          </label>
          <input type="number" min={0} value={draft.max_bonus_per_referral}
            onChange={(e) => setField('max_bonus_per_referral', Math.max(0, Math.floor(Number(e.target.value) || 0)))}
            className={INPUT_CLS + ' font-mono'} />
          <p className="text-[11px] text-arena-600 dark:text-neon-200/60 mt-1">Cap so a huge recharge doesn't mean a huge payout. Set 0 to disable the cap.</p>
        </div>
      </div>

      {/* 2026-05-20 (revised) — Referral Card icon picker. The image
          REPLACES the small card_giftcard glyph on every player's
          profile Referral card. Square PNG with transparent background;
          stored at platform_config.referral_bonus.referral_card_image_url. */}
      <div className="mt-4 rounded-lg bg-white dark:bg-arena-800/60 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-3">
        <div className="flex items-start gap-3 flex-wrap">
          {/* Square preview tile — mirrors the player-side icon
              footprint. Checkerboard backdrop so admins can SEE that
              the image they picked has transparency. */}
          <div
            className="shrink-0 w-20 h-20 rounded-lg overflow-hidden
                       ring-1 ring-arena-200/60 dark:ring-arena-600/40
                       grid place-items-center"
            style={{
              backgroundImage:
                'repeating-conic-gradient(#e5e7eb 0% 25%, #f9fafb 0% 50%)',
              backgroundSize: '16px 16px',
            }}
          >
            {draft.referral_card_image_url ? (
              <img
                src={draft.referral_card_image_url.replace(/\.png$/, '.thumb.png')}
                onError={(e) => { (e.target as HTMLImageElement).src = draft.referral_card_image_url || ''; }}
                alt="Referral icon preview"
                className="w-[88%] h-[88%] object-contain"
              />
            ) : (
              <Icon name="card_giftcard" size={28} className="text-arena-400 dark:text-neon-200/70 opacity-50" filled />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider font-bold text-arena-700 dark:text-neon-200/80 mb-1">
              Referral Card icon
            </div>
            <p className="text-[11px] text-arena-600 dark:text-neon-200/55 leading-relaxed mb-2">
              Replaces the default gift-icon on every player's profile
              Referral card. Square PNG with a transparent background,
              at least 100×100px. Leave empty to use the default.
            </p>
            <div className="flex items-center gap-2">
              <button type="button"
                onClick={() => setCardGalleryOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
                           text-[11px] font-bold uppercase tracking-wider
                           text-violet-700 dark:text-violet-200
                           bg-violet-50 hover:bg-violet-100
                           dark:bg-violet-500/10 dark:hover:bg-violet-500/20
                           ring-1 ring-inset ring-violet-400/40 transition">
                <Icon name="photo_library" size={12} />
                {draft.referral_card_image_url ? 'Change image' : 'Pick image'}
              </button>
              {draft.referral_card_image_url && (
                <button type="button"
                  onClick={() => setField('referral_card_image_url', null)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
                             text-[11px] font-bold uppercase tracking-wider
                             text-rose-700 dark:text-rose-300
                             bg-rose-500/5 hover:bg-rose-500/15
                             ring-1 ring-inset ring-rose-400/30 transition">
                  <Icon name="delete" size={12} /> Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-lg bg-white dark:bg-arena-800/60 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-3 flex items-center gap-3 flex-wrap">
        <Icon name="calculate" size={16} className="text-violet-700 dark:text-violet-300" />
        <span className="text-xs text-arena-400 dark:text-neon-200/70">Live preview — if a referred user recharges</span>
        <input type="number" min={0} value={previewAmount} onChange={(e) => setPreviewAmount(e.target.value)}
          className="w-24 rounded-lg bg-arena-100/60 dark:bg-arena-800 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-2 py-1.5 text-sm font-mono text-right focus:outline-none focus:ring-2 focus:ring-violet-400" />
        <span className="text-xs text-arena-400 dark:text-neon-200/70">→ referrer earns</span>
        <span className="text-lg font-bold text-violet-700 dark:text-violet-300">{preview ?? '—'}</span>
      </div>

      {err && <div className="mt-3 text-xs text-red-400">{err}</div>}

      <div className="mt-4 flex items-center gap-2">
        <button onClick={save} disabled={!dirty || busy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed transition">
          {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="save" size={14} />}
          {busy ? 'Saving…' : 'Save settings'}
        </button>
        {dirty && <button onClick={() => setDraft(cfg)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-arena-100/60 dark:bg-arena-800 hover:bg-arena-100/60 dark:bg-arena-700/60 text-arena-800 dark:text-neon-200/90 text-sm transition">
          <Icon name="undo" size={14} /> Undo
        </button>}
      </div>

      {cardGalleryOpen && (
        <MediaGalleryModal
          intendedUse="referral_card"
          title="Choose Referral Card icon"
          onPick={(a: MediaAsset) => {
            setField('referral_card_image_url', a.url);
            setCardGalleryOpen(false);
          }}
          onClose={() => setCardGalleryOpen(false)}
        />
      )}
    </div>
  );
}
